import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { enUS, nl } from 'date-fns/locale';
import { Plus, MagnifyingGlass, Funnel, CopySimple, ArrowClockwise } from '@phosphor-icons/react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import WerkvoorbereidingForm from '../components/WerkvoorbereidingForm';
import OfferDetailView from '../components/OfferDetailView';
import { useLanguage } from '../context/LanguageContext';
import { API } from '../config';
import { offerMatchesGlobalSearch } from '../utils/listSearch';

function parseOfferSortTime(offer) {
  const fields = [offer?.accepted_at, offer?.created_at];
  for (const value of fields) {
    const raw = String(value || '').trim();
    if (!raw) continue;
    if (/^\d+$/.test(raw)) {
      const n = Number(raw);
      const ms = n > 1e12 ? n : n * 1000;
      if (Number.isFinite(ms)) return ms;
    }
    const ms = Date.parse(raw);
    if (Number.isFinite(ms)) return ms;
  }
  return 0;
}

function parseOfferAmount(offer) {
  const n = Number(offer?.total_amount);
  return Number.isFinite(n) ? n : 0;
}

/** Background poll every 2h: Moneybird/GHL for new offers only (use Refresh for immediate sync). */
const OFFERS_SYNC_INTERVAL_MS = 2 * 60 * 60 * 1000;

function compareOffers(a, b, mode) {
  let cmp = 0;
  switch (mode) {
    case 'accepted_desc':
      cmp = parseOfferSortTime(b) - parseOfferSortTime(a);
      break;
    case 'accepted_asc':
      cmp = parseOfferSortTime(a) - parseOfferSortTime(b);
      break;
    case 'amount_desc':
      cmp = parseOfferAmount(b) - parseOfferAmount(a);
      break;
    case 'amount_asc':
      cmp = parseOfferAmount(a) - parseOfferAmount(b);
      break;
    case 'client_asc':
      cmp = String(a.contact?.name || '').localeCompare(String(b.contact?.name || ''), undefined, {
        sensitivity: 'base',
      });
      break;
    case 'client_desc':
      cmp = String(b.contact?.name || '').localeCompare(String(a.contact?.name || ''), undefined, {
        sensitivity: 'base',
      });
      break;
    case 'offer_number_asc':
      cmp = String(a.offer_number || '').localeCompare(String(b.offer_number || ''), undefined, { numeric: true });
      break;
    default:
      cmp = 0;
  }
  if (cmp !== 0) return cmp;
  return String(a.id || '').localeCompare(String(b.id || ''));
}

function parseOffersApiPayload(data) {
  if (Array.isArray(data)) {
    return { offers: data, lastUpdatedAt: null };
  }
  if (data && typeof data === 'object') {
    return {
      offers: Array.isArray(data.offers) ? data.offers : [],
      lastUpdatedAt: data.last_updated_at || null,
    };
  }
  return { offers: [], lastUpdatedAt: null };
}

const OffersPage = () => {
  const { t, language } = useLanguage();
  const [offers, setOffers] = useState([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showOfferDetail, setShowOfferDetail] = useState(false);
  const [viewOffer, setViewOffer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [offerSort, setOfferSort] = useState('accepted_desc');
  const [authRedirecting, setAuthRedirecting] = useState(false);
  const [syncingOffers, setSyncingOffers] = useState(false);

  useEffect(() => {
    fetchOffers();
    const syncTimer = window.setInterval(() => {
      syncOffersQuietly();
    }, OFFERS_SYNC_INTERVAL_MS);
    return () => window.clearInterval(syncTimer);
  }, []);

  const loadOffersFromApi = async () => {
    const offersRes = await axios.get(`${API}/offers`, { withCredentials: true });
    const { offers: list, lastUpdatedAt: updated } = parseOffersApiPayload(offersRes.data);
    setOffers(list);
    setLastUpdatedAt(updated);
  };

  const runOffersSync = async ({ quiet = false } = {}) => {
    const syncRes = await axios.post(`${API}/offers/sync`, {}, { withCredentials: true });
    const added = Number(syncRes.data?.added) || 0;
    if (syncRes.data?.last_updated_at) {
      setLastUpdatedAt(syncRes.data.last_updated_at);
    }
    await loadOffersFromApi();
    if (quiet) {
      if (added > 0) {
        toast.success(
          added === 1 ? t('offersSyncAddedOne') : t('offersSyncAddedMany').replace('{count}', String(added)),
        );
      }
      return added;
    }
    if (added > 0) {
      toast.success(
        added === 1 ? t('offersSyncAddedOne') : t('offersSyncAddedMany').replace('{count}', String(added)),
      );
    } else {
      toast.success(t('offersRefreshUpToDate'));
    }
    return added;
  };

  const syncOffersQuietly = async () => {
    try {
      await runOffersSync({ quiet: true });
    } catch {
      // Background sync — do not disturb the user.
    }
  };

  const handleRefreshOffers = async () => {
    if (syncingOffers) return;
    setSyncingOffers(true);
    try {
      await runOffersSync({ quiet: false });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('offersRefreshFailed'));
    } finally {
      setSyncingOffers(false);
    }
  };

  const fetchOffers = async () => {
    try {
      await loadOffersFromApi();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const isMoneybirdAccessIssue =
        typeof detail === 'string' &&
        (
          detail.toLowerCase().includes('no access to') ||
          detail.toLowerCase().includes('invalid token') ||
          detail.toLowerCase().includes('token is invalid') ||
          detail.toLowerCase().includes('re-authorize')
        );

      if (isMoneybirdAccessIssue && !authRedirecting) {
        try {
          setAuthRedirecting(true);
          const verify = await axios.get(`${API}/integrations/moneybird/verify`, {
            withCredentials: true,
          });
          const authorizeUrl = verify?.data?.authorize_url;
          if (verify?.data?.needs_authorization && authorizeUrl) {
            toast.info(t('moneybirdAuthRedirect'));
            window.location.href = authorizeUrl;
            return;
          }
        } catch (_) {
          // Fall back to normal error toast below.
        } finally {
          setAuthRedirecting(false);
        }
      }

      toast.error(typeof detail === 'string' ? detail : t('failedFetchOffers'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOffer = (offer) => {
    setSelectedOffer(offer);
    setShowForm(true);
  };

  const handleManualOrder = () => {
    setSelectedOffer({ manual: true });
    setShowForm(true);
  };

  const handleViewOffer = async (offer) => {
    try {
      const response = await axios.get(
        `${API}/offers/${encodeURIComponent(offer.id)}`,
        { withCredentials: true },
      );
      setViewOffer(response.data);
      setShowOfferDetail(true);
    } catch {
      toast.error(t('failedLoadOfferDetails'));
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedOffer(null);
  };

  const handleOrderCreated = () => {
    toast.success(t('orderCreatedSuccess'));
    handleFormClose();
    void loadOffersFromApi();
  };

  const formatOfferNumber = (offerNumber) => {
    const normalized = String(offerNumber || '').trim();
    const maxLen = 11; // OV-25082901
    if (normalized.length <= maxLen) {
      return normalized;
    }
    return `${normalized.slice(0, maxLen)}.`;
  };

  const copyOfferNumber = async (e, offerNumber) => {
    e.stopPropagation();
    const full = String(offerNumber || '').trim();
    if (!full) {
      return;
    }
    try {
      await navigator.clipboard.writeText(full);
      toast.success(`${t('offerIdCopiedWithValue')}: ${full}`);
    } catch {
      toast.error(t('failedCopyOfferId'));
    }
  };

  const filteredSortedOffers = useMemo(() => {
    const filtered = offers.filter((offer) => offerMatchesGlobalSearch(offer, searchQuery));
    const sorted = [...filtered].sort((a, b) => compareOffers(a, b, offerSort));
    return sorted;
  }, [offers, searchQuery, offerSort]);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdatedAt) return null;
    try {
      const dt = parseISO(String(lastUpdatedAt));
      if (Number.isNaN(dt.getTime())) return null;
      const loc = language === 'en' ? enUS : nl;
      return format(dt, 'd MMM yyyy, HH:mm', { locale: loc });
    } catch {
      return null;
    }
  }, [lastUpdatedAt, language]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00CBA9] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="offers-page" className="animate-fadeIn">
      {/* Header */}
      {!showOfferDetail && (
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">{t('offers')}</h1>
          <p className="page-subtitle">
            {t('acceptedOffers')} • {offers.length} {t('total')}
          </p>
          {lastUpdatedLabel ? (
            <p className="text-sm text-[#64748b] mt-1" data-testid="offers-last-updated">
              {t('offersLastUpdated')}: {lastUpdatedLabel}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            className="vmk-btn vmk-btn-primary shrink-0 inline-flex items-center gap-2"
            onClick={handleManualOrder}
            data-testid="manual-order-button"
          >
            <Plus size={18} weight="bold" aria-hidden />
            {t('manualOrder')}
          </button>
          <button
            type="button"
            className="vmk-btn vmk-btn-secondary shrink-0 inline-flex items-center gap-2"
            onClick={() => void handleRefreshOffers()}
            disabled={syncingOffers}
            data-testid="offers-refresh-button"
            title={t('offersRefresh')}
            aria-label={t('offersRefresh')}
          >
            <ArrowClockwise size={18} className={syncingOffers ? 'animate-spin' : ''} aria-hidden />
            {t('offersRefresh')}
          </button>
        </div>
      </div>
      )}

      {/* Search & sort */}
      {!showOfferDetail && (
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
        <div className="relative min-w-0 w-full flex-1">
          <MagnifyingGlass
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-[#94a3b8]"
            aria-hidden
          />
          <input
            type="text"
            placeholder={t('searchOffers')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="vmk-input w-full min-w-0 !pl-[2.75rem] !pr-4"
            data-testid="search-offers"
          />
        </div>
        <div className="w-full shrink-0 sm:w-48 sm:max-w-[12rem]">
          <Select value={offerSort} onValueChange={setOfferSort}>
            <SelectTrigger
              className="vmk-input h-11 w-full min-w-0 border-[#e2e8f0] bg-white pl-2.5 pr-2 text-left [&>span]:min-w-0"
              data-testid="offers-sort-select"
              aria-label={t('offerSortLabel')}
            >
              <Funnel size={16} className="mr-1.5 shrink-0 text-[#64748b]" aria-hidden />
              <SelectValue className="truncate" placeholder={t('offerSortLabel')} />
            </SelectTrigger>
            <SelectContent className="min-w-[var(--radix-select-trigger-width)] max-w-[min(100vw-2rem,20rem)]">
            <SelectItem value="accepted_desc">{t('offerSortAcceptedRecent')}</SelectItem>
            <SelectItem value="accepted_asc">{t('offerSortAcceptedOldest')}</SelectItem>
            <SelectItem value="amount_desc">{t('offerSortAmountHigh')}</SelectItem>
            <SelectItem value="amount_asc">{t('offerSortAmountLow')}</SelectItem>
            <SelectItem value="client_asc">{t('offerSortClientAZ')}</SelectItem>
            <SelectItem value="client_desc">{t('offerSortClientZA')}</SelectItem>
            <SelectItem value="offer_number_asc">{t('offerSortOfferNumber')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      )}

      {showOfferDetail && viewOffer ? (
        <OfferDetailView
          offer={viewOffer}
          onBack={() => {
            setShowOfferDetail(false);
            setViewOffer(null);
          }}
          onCreateOrder={(offer) => {
            setSelectedOffer(offer);
            setShowForm(true);
          }}
        />
      ) : (
      <div className="table-container">
        <table className="vmk-table">
          <thead>
            <tr>
              <th>{t('offerNumber')}</th>
              <th>{t('client')}</th>
              <th>{t('address')}</th>
              <th>{t('contact')}</th>
              <th>{t('amount')}</th>
              <th>{t('source')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredSortedOffers.map((offer) => (
              <tr
                key={offer.id}
                className="cursor-pointer hover:bg-[#f8fafc] transition-colors"
                onClick={() => handleViewOffer(offer)}
                data-testid={`offer-row-${offer.id}`}
              >
                <td className="whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span
                      className="font-mono text-[#00CBA9] font-medium"
                      title={offer.offer_number}
                    >
                      {formatOfferNumber(offer.offer_number)}
                    </span>
                    <button
                      type="button"
                      title={`Copy full ID: ${offer.offer_number}`}
                      aria-label={`Copy full ID ${offer.offer_number}`}
                      onClick={(e) => copyOfferNumber(e, offer.offer_number)}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#e2e8f0] text-[#64748b] hover:text-[#00CBA9] hover:border-[#00CBA9] hover:bg-[#ecfeff] transition-colors"
                    >
                      <CopySimple size={14} />
                    </button>
                  </div>
                </td>
                <td>
                  <span className="font-semibold">{offer.contact.name}</span>
                </td>
                <td>
                  <div>
                    <div className="text-sm">{offer.contact.address}</div>
                    <div className="text-xs text-[#94a3b8]">{offer.contact.postcode}</div>
                  </div>
                </td>
                <td>
                  <div>
                    <div className="text-sm font-mono">{offer.contact.phone}</div>
                    <div className="text-xs text-[#94a3b8]">{offer.contact.email}</div>
                  </div>
                </td>
                <td>
                  <span className="font-semibold">
                    €{Number(offer.total_amount || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
                  </span>
                </td>
                <td>
                  {String(offer?.id || '').startsWith('ghl:') ? (
                    <span className="inline-flex items-center rounded-full border border-[#dbeafe] bg-[#eff6ff] px-2.5 py-1 text-xs font-semibold text-[#1d4ed8]">
                      {t('sourceGhl')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full border border-[#dcfce7] bg-[#f0fdf4] px-2.5 py-1 text-xs font-semibold text-[#166534]">
                      {t('sourceMoneybird')}
                    </span>
                  )}
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectOffer(offer);
                    }}
                    className="vmk-btn vmk-btn-primary min-w-[138px] justify-center shadow-sm hover:shadow-md"
                    data-testid={`create-order-btn-${offer.id}`}
                  >
                    <Plus size={16} weight="bold" />
                    {t('createOrder')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredSortedOffers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#94a3b8]">{t('noOffersFound')}</p>
          </div>
        )}
      </div>
      )}

      {/* Form Sheet (Side Panel) */}
      <Sheet open={showForm} onOpenChange={setShowForm}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl p-0 overflow-y-auto bg-[#f8f9fb]"
        >
          <SheetHeader className="px-6 py-5 border-b border-[#e2e8f0] bg-white sticky top-0 z-10">
            <SheetTitle className="text-xl font-bold">
              {selectedOffer?.manual ? t('manualOrderFormTitle') : t('werkvoorbereidingForm')}
            </SheetTitle>
            <SheetDescription className="sr-only">{t('werkvoorbereidingForm')}</SheetDescription>
          </SheetHeader>
          <div className="p-6">
            {selectedOffer && (
              <WerkvoorbereidingForm
                offer={selectedOffer}
                onClose={handleFormClose}
                onSuccess={handleOrderCreated}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default OffersPage;
